import { Dog, BirthRecord, MatingRecord } from '@/types/dog';
import {
  AIPrompt,
  PromptWeights,
  InbreedingThresholds,
  DEFAULT_PROMPT_WEIGHTS,
  DEFAULT_INBREEDING_THRESHOLDS,
} from '@/types/aiManagement';

/**
 * Gemini APIに送信するプロンプトを構築するクラス
 */
export class PromptBuilder {
  private weights: PromptWeights = DEFAULT_PROMPT_WEIGHTS;
  private thresholds: InbreedingThresholds = DEFAULT_INBREEDING_THRESHOLDS;
  private customInstructions?: string;

  /**
   * AIPrompt設定を適用
   */
  setPromptConfig(prompt: AIPrompt): void {
    this.weights = prompt.weights;
    this.thresholds = prompt.inbreedingThresholds;
    this.customInstructions = prompt.customInstructions;
  }

  /**
   * 設定をデフォルトにリセット
   */
  resetConfig(): void {
    this.weights = DEFAULT_PROMPT_WEIGHTS;
    this.thresholds = DEFAULT_INBREEDING_THRESHOLDS;
    this.customInstructions = undefined;
  }
  /**
   * 交配提案用のプロンプトを生成
   * @param female 対象メス犬
   * @param candidates 候補オス犬リスト
   * @param birthRecords 出産記録
   * @param matingRecords 交配記録
   * @param inbreedingCoefficients 各オス犬との近親係数マップ
   * @returns Gemini APIに送信するプロンプト
   */
  buildMatingProposalPrompt(
    female: Dog,
    candidates: Dog[],
    birthRecords: BirthRecord[],
    matingRecords: MatingRecord[],
    inbreedingCoefficients: Map<string, number>
  ): string {
    const systemPrompt = this.buildSystemPrompt();
    const femaleSection = this.buildFemaleSection(female, birthRecords);
    const candidatesSection = this.buildCandidatesSection(
      candidates,
      birthRecords,
      matingRecords,
      inbreedingCoefficients
    );
    const outputFormatSection = this.buildOutputFormatSection();

    return `${systemPrompt}

${femaleSection}

${candidatesSection}

${outputFormatSection}`;
  }

  /**
   * システムプロンプトを生成（動的な重み付けと閾値を適用）
   */
  private buildSystemPrompt(): string {
    const { geneticDiversity, breedingHistory, traitMatch } = this.weights;
    const { safe, caution, warning } = this.thresholds;

    let prompt = `あなたは犬のブリーディング専門家です。繁殖学と遺伝学の深い知識を持ち、健康で優れた子犬を産出するための最適な交配相手を提案します。

以下の評価基準に基づいて、メス犬に最適なオス犬を評価してください:

## 評価基準
1. **遺伝的多様性（${geneticDiversity}%）**: 近親係数が低いほど高評価。近親交配は遺伝的疾患のリスクを高めます。
   - 近親係数 < ${safe}%: 安全（高評価）
   - 近親係数 ${safe}% - ${caution}%: 注意（中評価）
   - 近親係数 ${caution}% - ${warning}%: 警告（低評価）
   - 近親係数 >= ${warning}%: 危険（非推奨）

2. **繁殖実績（${breedingHistory}%）**: 過去の出産データに基づく成功率を評価
   - 出産頭数、生存率、帝王切開率などを考慮
   - 実績がない場合は中立スコア

3. **特徴マッチング（${traitMatch}%）**: 性格、体格、毛色の相性を評価
   - 極端な特徴の組み合わせを避ける
   - 犬種標準に近い子犬が期待できるか

## 重要な注意事項
- 近親係数が${caution}%を超える組み合わせには必ず警告を付けてください
- データが不足している場合は、その旨を明記してください
- 提案は参考情報であり、最終判断はブリーダーが行うことを前提としてください`;

    // カスタム指示がある場合は追加
    if (this.customInstructions) {
      prompt += `

## 追加指示
${this.customInstructions}`;
    }

    return prompt;
  }

  /**
   * メス犬情報セクションを生成
   */
  private buildFemaleSection(female: Dog, birthRecords: BirthRecord[]): string {
    const age = this.calculateAge(female.birthDate.toDate());
    const femaleBirthRecords = birthRecords.filter(
      (record) => record.motherId === female.id
    );
    const breedingStats = this.calculateBreedingStats(femaleBirthRecords);

    return `## 対象メス犬
- **名前**: ${female.name}
- **生体番号**: ${female.bioNumber}
- **年齢**: ${age}歳
- **性格**: ${female.characteristics?.temperament || '不明'}
- **体格**: ${female.characteristics?.size || '不明'}
- **毛色**: ${female.characteristics?.color || '不明'}

### 繁殖履歴
- 出産回数: ${breedingStats.totalBirths}回
- 平均出産頭数: ${breedingStats.avgPuppies}頭
- 平均生存率: ${breedingStats.survivalRate}%
- 帝王切開率: ${breedingStats.cesareanRate}%`;
  }

  /**
   * 候補オス犬セクションを生成
   */
  private buildCandidatesSection(
    candidates: Dog[],
    birthRecords: BirthRecord[],
    matingRecords: MatingRecord[],
    inbreedingCoefficients: Map<string, number>
  ): string {
    const candidateEntries = candidates.map((male, index) => {
      const age = this.calculateAge(male.birthDate.toDate());
      const inbreedingCoef = inbreedingCoefficients.get(male.id) || 0;
      const inbreedingPercent = (inbreedingCoef * 100).toFixed(2);

      // このオス犬の繁殖実績
      const maleBirthRecords = birthRecords.filter(
        (record) => record.fatherId === male.id
      );
      const maleStats = this.calculateBreedingStats(maleBirthRecords);

      // このオス犬の交配回数
      const matingCount = matingRecords.filter(
        (record) => record.maleId === male.id
      ).length;

      return `### 候補${index + 1}: ${male.name}
- **ID**: ${male.id}
- **生体番号**: ${male.bioNumber}
- **年齢**: ${age}歳
- **性格**: ${male.characteristics?.temperament || '不明'}
- **体格**: ${male.characteristics?.size || '不明'}
- **毛色**: ${male.characteristics?.color || '不明'}
- **近親係数**: ${inbreedingPercent}%
- **交配回数**: ${matingCount}回
- **父犬としての実績**: 出産${maleStats.totalBirths}回、平均${maleStats.avgPuppies}頭、生存率${maleStats.survivalRate}%`;
    });

    return `## 候補オス犬（${candidates.length}頭）
${candidateEntries.join('\n\n')}`;
  }

  /**
   * 出力フォーマットセクションを生成
   */
  private buildOutputFormatSection(): string {
    return `## 出力形式
以下のJSON形式で回答してください。必ずJSONのみを出力し、他のテキストは含めないでください。

\`\`\`json
{
  "suggestions": [
    {
      "maleId": "オス犬のID",
      "score": 85,
      "scoreBreakdown": {
        "geneticDiversity": 90,
        "breedingHistory": 80,
        "traitMatch": 85
      },
      "reasons": [
        "推奨理由1",
        "推奨理由2",
        "推奨理由3"
      ],
      "warnings": [
        "警告がある場合のみ記載"
      ],
      "expectedTraits": "期待される子犬の特徴を1-2文で記載"
    }
  ]
}
\`\`\`

**重要**:
- スコアは0-100の整数で評価してください
- 候補を全てスコア順（降順）でソートして出力してください
- 理由は具体的かつ簡潔に3つ程度記載してください
- 警告は必要な場合のみ記載してください（近親係数が高い場合など）`;
  }

  /**
   * 年齢を計算
   */
  private calculateAge(birthDate: Date): number {
    const now = new Date();
    let age = now.getFullYear() - birthDate.getFullYear();
    const monthDiff = now.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && now.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  }

  /**
   * 繁殖統計を計算
   */
  private calculateBreedingStats(birthRecords: BirthRecord[]): {
    totalBirths: number;
    avgPuppies: string;
    survivalRate: string;
    cesareanRate: string;
  } {
    const totalBirths = birthRecords.length;

    if (totalBirths === 0) {
      return {
        totalBirths: 0,
        avgPuppies: 'N/A',
        survivalRate: 'N/A',
        cesareanRate: 'N/A',
      };
    }

    const totalPuppies = birthRecords.reduce(
      (sum, record) => sum + record.totalPuppies,
      0
    );
    const totalAlive = birthRecords.reduce(
      (sum, record) => sum + record.alivePuppies,
      0
    );
    const cesareanCount = birthRecords.filter(
      (record) => record.deliveryType === 'cesarean'
    ).length;

    const avgPuppies = (totalPuppies / totalBirths).toFixed(1);
    const survivalRate =
      totalPuppies > 0
        ? ((totalAlive / totalPuppies) * 100).toFixed(0)
        : 'N/A';
    const cesareanRate = ((cesareanCount / totalBirths) * 100).toFixed(0);

    return {
      totalBirths,
      avgPuppies,
      survivalRate,
      cesareanRate,
    };
  }
}

// シングルトンインスタンスをエクスポート
export const promptBuilder = new PromptBuilder();
